/**
 * Photo Search Service Abstraction
 * 
 * Provides configuration and integration endpoints for the third-party
 * personal face-recognition / photo-search experience.
 * 
 * Future iterations can replace or enhance this abstraction with
 * direct API token exchange, SSO handshakes, or webhook integrations
 * without changing page-level components.
 */

// Default active photo search album
const DEFAULT_PHOTO_SEARCH_URL = 'https://toastwall.com/album/wa-ganesh-fest-2026-b9940d?token=vats5y';

// Primary environment variable convention for CRA: REACT_APP_PERSONAL_PHOTO_SEARCH_URL
// Secondary fallback supported for cross-framework compatibility: VITE_PERSONAL_PHOTO_SEARCH_URL
const CONFIGURED_URL = (
  process.env.REACT_APP_PERSONAL_PHOTO_SEARCH_URL ||
  process.env.VITE_PERSONAL_PHOTO_SEARCH_URL ||
  DEFAULT_PHOTO_SEARCH_URL
).trim();

// Placeholder detection
const isPlaceholder = (url) => {
  if (!url) return true;
  const lowercase = url.toLowerCase();
  return (
    lowercase.includes('your-photo-search-url') ||
    lowercase.includes('example.com') ||
    lowercase.includes('placeholder')
  );
};

/**
 * Returns configuration status and URL for the third-party photo search experience.
 */
export const getPhotoSearchStatus = () => {
  if (!CONFIGURED_URL || isPlaceholder(CONFIGURED_URL)) {
    return {
      isConfigured: false,
      url: null,
      message: 'Personal photo search is currently undergoing maintenance. Please check back shortly or browse our public gallery.'
    };
  }

  // Validate URL structure
  try {
    const parsed = new URL(CONFIGURED_URL);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return {
        isConfigured: false,
        url: null,
        message: 'Photo search service URL is not valid. Please contact support.'
      };
    }

    return {
      isConfigured: true,
      url: CONFIGURED_URL,
      message: 'Photo search service is available.'
    };
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[PhotoSearchService] Invalid URL configured for REACT_APP_PERSONAL_PHOTO_SEARCH_URL:', CONFIGURED_URL);
    }
    return {
      isConfigured: false,
      url: null,
      message: 'Photo search service URL is invalid.'
    };
  }
};

/**
 * Initiates the third-party personal photo search session.
 * 
 * Opens the external photo-search provider in a new secure tab.
 * Requires explicit user consent before proceeding.
 * 
 * @param {Object} options
 * @param {Object} options.user Current authenticated BOW user profile
 * @param {boolean} options.consentGiven Explicit confirmation that user accepts third-party processing
 * @returns {{ success: boolean, openedUrl?: string, error?: string }}
 */
export const launchPhotoSearchSession = ({ user, consentGiven }) => {
  if (!consentGiven) {
    throw new Error('User consent is required before opening the external photo-search service.');
  }

  const status = getPhotoSearchStatus();
  if (!status.isConfigured || !status.url) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[PhotoSearchService] Unable to launch session: Service URL is not configured.');
    }
    return {
      success: false,
      error: status.message
    };
  }

  // Log session launch in non-production without leaking sensitive user information
  if (process.env.NODE_ENV !== 'production') {
    console.log('[PhotoSearchService] Launching photo search session for user:', user?.email || user?.uid || 'authenticated-user');
  }

  // Open the third-party service securely in a new browser tab
  const newWindow = window.open(status.url, '_blank', 'noopener,noreferrer');
  
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    // Pop-up was likely blocked by the browser
    return {
      success: false,
      openedUrl: status.url,
      error: 'Pop-up was blocked by your browser. Please allow pop-ups for this site or open the link directly.'
    };
  }

  return {
    success: true,
    openedUrl: status.url
  };
};
